export type JustifiedTile = { id: string; aspectRatio: number };

export type JustifiedRow<T extends JustifiedTile> = {
  height: number;
  tiles: Array<T & { width: number }>;
};

type LayoutJustifiedRowsParams<T extends JustifiedTile> = {
  tiles: T[];
  containerWidth: number;
  targetHeight: number;
  gap: number;
};

type Accumulator<T extends JustifiedTile> = {
  rows: JustifiedRow<T>[];
  current: T[];
};

const getRatioSum = <T extends JustifiedTile>(rowTiles: T[]) =>
  rowTiles.reduce((sum, tile) => sum + tile.aspectRatio, 0);

export const layoutJustifiedRows = <T extends JustifiedTile>({
  tiles,
  containerWidth,
  targetHeight,
  gap,
}: LayoutJustifiedRowsParams<T>): JustifiedRow<T>[] => {
  if (containerWidth <= 0 || tiles.length === 0) {
    return [];
  }

  const getFittedHeight = (rowTiles: T[]) =>
    (containerWidth - gap * (rowTiles.length - 1)) / getRatioSum(rowTiles);

  const toRow = (rowTiles: T[], height: number): JustifiedRow<T> => ({
    height,
    tiles: rowTiles.map((tile) => ({
      ...tile,
      width: tile.aspectRatio * height,
    })),
  });

  const { rows, current } = tiles.reduce<Accumulator<T>>(
    (accumulator, tile) => {
      const candidate = [...accumulator.current, tile];
      const candidateWidth =
        getRatioSum(candidate) * targetHeight + gap * (candidate.length - 1);

      if (candidateWidth < containerWidth) {
        return { ...accumulator, current: candidate };
      }

      // The row overflows: close it with or without the new tile, whichever
      // stays closer to the target height, so one panorama cannot flatten
      // the whole row.
      const withTileHeight = getFittedHeight(candidate);
      const withoutTileHeight =
        accumulator.current.length > 0
          ? getFittedHeight(accumulator.current)
          : Number.POSITIVE_INFINITY;
      const keepTile =
        Math.abs(withTileHeight - targetHeight) <=
        Math.abs(withoutTileHeight - targetHeight);

      return keepTile
        ? {
            rows: [...accumulator.rows, toRow(candidate, withTileHeight)],
            current: [],
          }
        : {
            rows: [
              ...accumulator.rows,
              toRow(accumulator.current, withoutTileHeight),
            ],
            current: [tile],
          };
    },
    { rows: [], current: [] },
  );

  if (current.length === 0) {
    return rows;
  }

  // A trailing row never grows past the target; a single oversized tile
  // still shrinks to fit the container.
  const trailingHeight = Math.min(targetHeight, getFittedHeight(current));

  return [...rows, toRow(current, trailingHeight)];
};
